import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

export interface PlanPricing {
  region: string;
  currency: string;
  symbol: string;
  label: string;
  taxLabel: string;
  monthlyBilledAnnually: number | null;
  monthly: number | null;
  extraOutletPerMonth: number | null;
  messagePack: number | null;
  messagePackSize: number | null;
  isCustom: boolean;
  isFree: boolean;
}

export interface PlanCard {
  code: 'starter' | 'growth' | 'pro' | 'enterprise';
  name: string;
  tagline: string;
  includedOutlets: number;
  popular?: boolean;
  highlights: string[];
  cta: string;
  trialDays?: number;
  limits: Record<string, unknown>;
  pricing: PlanPricing;
}

export interface PlanCatalog {
  region: string;
  currency: string;
  symbol: string;
  label: string;
  taxLabel: string;
  plans: PlanCard[];
}

export interface SubscriptionSummary {
  subscription: {
    planCode: PlanCard['code'];
    status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'expired' | 'paused';
    trialEndsAt?: string;
    currentPeriodEnd?: string;
    limits?: Record<string, unknown>;
  };
  periodKey: string;
  usage: Array<{ metric: string; value: number }>;
  limits: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class BillingApiService {
  private readonly api = inject(ApiService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly tokenKey = 'TtZUw0Q87E647ac8j0gQHiCmBhczph';

  getPlans(region?: string): Observable<any> {
    const query = region ? `?region=${region}` : '';
    return this.api.commonGetMethod(`billing/plans${query}`, this.authHeader());
  }

  getCurrent(): Observable<any> {
    return this.api.commonGetMethod('billing/current', this.authHeader());
  }

  getUsage(): Observable<any> {
    return this.api.commonGetMethod('billing/usage', this.authHeader());
  }

  selectPlan(planCode: PlanCard['code']): Observable<any> {
    return this.api.commonPostMethod('billing/select-plan', { planCode }, this.authHeader());
  }

  upgradeIntent(planCode: PlanCard['code'], redirectUrl?: string): Observable<any> {
    return this.api.commonPostMethod(
      'billing/upgrade-intent',
      { planCode, redirectUrl },
      this.authHeader(),
    );
  }

  private authHeader() {
    const token = isPlatformBrowser(this.platformId)
      ? localStorage.getItem(this.tokenKey) || ''
      : '';
    return { headers: { Authorization: `Bearer ${token}` } };
  }
}
